/**
 * Track 2: public battle report page (/r/<token>) – no login, read-only. It only ever receives the sanitised
 * replay (slot ids, class-based names, numbers) and plays it with the same cinematic player as in the game.
 */
import { useEffect, useMemo, useState } from 'react';
import { publicToBattleReplay, isShareToken, type PublicBattleReplay } from '@galaxis/shared';
import { fetchSharedBattle } from '../api/galaxy.ts';
import { BattleView } from './BattleView.tsx';
import { AudioControl } from './AudioControl.tsx';
import { APP_NAME } from '../brand.ts';

/** `/r/<token>` → token, else null. */
export function sharedTokenFromPath(pathname: string): string | null {
  const m = /^\/r\/([^/]+)\/?$/.exec(pathname);
  return m ? decodeURIComponent(m[1]!) : null;
}

export function SharedReplayPage({ token }: { token: string }) {
  const [data, setData] = useState<PublicBattleReplay | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    if (!isShareToken(token)) { setData(null); return; }
    void fetchSharedBattle(token).then(r => { if (live) setData(r); });
    return () => { live = false; };
  }, [token]);

  const replay = useMemo(() => (data ? publicToBattleReplay(data) : null), [data]);
  const sides = useMemo(() => {
    if (!data) return null;
    const count = (side: 'attacker' | 'defender') => data.ships.filter(s => s.side === side).length;
    const lost = (side: 'attacker' | 'defender') => data.log.filter(e => e.type === 'ship_destroyed' && data.ships.find(s => s.id === e.targetId)?.side === side).length;
    return { a: count('attacker'), d: count('defender'), aLost: lost('attacker'), dLost: lost('defender') };
  }, [data]);

  return (
    <div className="min-h-full w-full bg-space-900 text-slate-200 flex flex-col items-center p-4 gap-3" data-testid="shared-replay-page">
      <header className="w-full max-w-[720px] flex items-center justify-between">
        <a href="/" className="font-mono font-semibold tracking-widest text-accent text-sm uppercase">{APP_NAME}</a>
        <AudioControl />
      </header>
      <main className="w-full max-w-[720px] flex flex-col gap-3">
        <h1 className="text-lg font-semibold text-slate-100">Megosztott csatajelentés</h1>
        {data === undefined && <p role="status" className="text-sm text-slate-400">Betöltés…</p>}
        {data === null && (
          <p role="alert" className="notice notice-error text-sm p-3" data-testid="shared-replay-missing">
            Ez a csatajelentés nem található, vagy a tulajdonosa visszavonta a megosztást.
          </p>
        )}
        {replay && sides && (
          <>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-slate-900/60 rounded px-3 py-2"><p className="text-slate-500">Támadó</p><p className="font-mono text-sky-300">{sides.a} hajó · {sides.aLost} elveszett</p></div>
              <div className="bg-slate-900/60 rounded px-3 py-2"><p className="text-slate-500">Védő</p><p className="font-mono text-rose-300">{sides.d} hajó · {sides.dLost} elveszett</p></div>
            </div>
            <BattleView replay={replay} onClose={() => { window.location.assign('/'); }} autoPlay />
            <p className="text-xs text-slate-500">Játssz te is a {APP_NAME}-ben: <a className="text-accent underline" href="/">belépés</a></p>
          </>
        )}
      </main>
    </div>
  );
}
